import { useMemo, type ComponentType } from 'react';
import { useSearchParams } from 'react-router-dom';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import DataLoadError from '@/components/errors/DataLoadError';
import { shouldShowDataLoadError, isNetworkApiError } from '@/app/common/utils/apiErrorUtils';
import ToolsBreadcrumb from '@/app/common/components/ToolsBreadcrumb';
import { useGetWalletTransactionsCountQuery, useGetWalletTransactionsMutation } from '@/app/Wallet/api/walletApi';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { transactionsColumns } from '@/app/Wallet/config/transactionsColumns';
import {
  WALLET_TYPE_AI,
  WALLET_TYPE_PERSONAL,
  type WalletTransactionItem,
  type WalletType,
} from '@/app/Wallet/types/walletApiTypes';

type AgGridWrapperProps = {
  key?: WalletType;
  height: string;
  columns: unknown[];
  data?: WalletTransactionItem[];
  total?: number;
  perPage?: number;
  fetchFunction?: unknown;
  additionalData?: Record<string, unknown>;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

function resolveWalletType(value: string | null): WalletType {
  return value === String(WALLET_TYPE_AI) ? WALLET_TYPE_AI : WALLET_TYPE_PERSONAL;
}

const WalletTransactions = () => {
  const [searchParams] = useSearchParams();
  const walletType = resolveWalletType(searchParams.get('wallet_type'));
  const isAiWallet = walletType === WALLET_TYPE_AI;

  const additionalData = useMemo(() => ({ wallet_type: walletType }), [walletType]);

  const { data: walletTransactionsCount, isLoading } = useGetWalletTransactionsCountQuery({
    page: 1,
    perPage: 50,
    ...additionalData,
  });
  const [getWalletTransactions, { isError, error, reset, isLoading: isMutationLoading }] =
    useGetWalletTransactionsMutation();

  const pageTitle = isAiWallet ? 'تراکنش‌های کیف پول AI' : 'تراکنش های کیف پول';
  const pageUrl = `/wallet/transactions?wallet_type=${walletType}`;

  if (shouldShowDataLoadError(isError, error)) {
    return (
      <DataLoadError
        onRetry={() => reset()}
        isRetrying={isMutationLoading}
        isNetworkError={isNetworkApiError(error)}
      />
    );
  }

  return (
    <>
      <ToolsBreadcrumb parentName={pageTitle} parentUrl={pageUrl} childs={[]} />
      <div className="for-style">
        {!isLoading ? (
          <TypedAgGridWrapper
            key={walletType}
            data={walletTransactionsCount?.rows}
            columns={transactionsColumns}
            height="700px"
            total={walletTransactionsCount?.total}
            perPage={50}
            fetchFunction={getWalletTransactions}
            additionalData={additionalData}
          />
        ) : (
          <AgGridSkeleton height="700px" />
        )}
      </div>
      <style>
        {`
          .for-style .ag-row-focus {
            z-index: 2;
          }
        `}
      </style>
    </>
  );
};

export default WalletTransactions;
